import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import {
  type AccountRole,
  type AccountSummary,
  useGenerateProfile,
  usePatchAccount,
  useUpdateTgProfile,
} from "@/lib/accounts";
import { useProxies } from "@/lib/proxies";
import {
  useInboxState,
  useSetInboxEnabled,
} from "@/lib/inbox";
import {
  useMaintenanceStatus,
  useSetMaintenanceEnabled,
} from "@/lib/warmup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { pushToast } from "@/components/Toaster";

/**
 * AccountSettingsTab — TG-profile edit + account fields (role/proxy/notes) +
 * maintenance & inbox toggles, all top-level (no nested disclosure).
 */
export function AccountSettingsTab({ account }: { account: AccountSummary }) {
  return (
    <div className="space-y-4">
      <AccountFieldsCard account={account} />
      <ToggleCard account={account} />
      <TgProfileCard account={account} />
    </div>
  );
}

function AccountFieldsCard({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const proxies = useProxies();
  const patch = usePatchAccount();
  const [role, setRole] = useState<AccountRole>(account.role);
  const [proxyId, setProxyId] = useState<string>(account.proxy_id ?? "__none__");
  const [notes, setNotes] = useState<string>(account.notes ?? "");
  // Pause/resume: allowed from active AND warming_up (you can freeze a
  // mid-warmup account). banned/flood_wait/auth_required stay system-managed.
  const pausable =
    account.status === "active" ||
    account.status === "paused" ||
    account.status === "warming_up";
  const [paused, setPaused] = useState(account.status === "paused");

  // Re-sync when switching to a different account in the drawer.
  useEffect(() => {
    setRole(account.role);
    setProxyId(account.proxy_id ?? "__none__");
    setNotes(account.notes ?? "");
    setPaused(account.status === "paused");
  }, [account.id, account.role, account.proxy_id, account.notes, account.status]);

  const proxyVal = proxyId === "__none__" ? null : proxyId;
  const statusDirty = pausable && paused !== (account.status === "paused");
  const dirty =
    statusDirty ||
    role !== account.role ||
    proxyVal !== (account.proxy_id ?? null) ||
    notes !== (account.notes ?? "");

  const save = async () => {
    try {
      const updated = await patch.mutateAsync({
        id: account.id,
        status: statusDirty ? (paused ? "paused" : "active") : undefined,
        // demoted_scraper is a derived/penalty state — never write it back.
        role: role === "demoted_scraper" ? undefined : role,
        proxy_id: proxyVal,
        // Sent as typed: an emptied field must clear the stored note.
        notes,
      });
      // Non-blocking proxy advisories: type↔session mismatch (§3) and stacking
      // accounts on a non-mobile proxy (§1). Show each as a sticky warning.
      const warnings = [updated.proxy_coherence_warning, updated.proxy_capacity_warning].filter(
        Boolean,
      ) as string[];
      if (warnings.length > 0) {
        for (const detail of warnings) {
          pushToast({
            kind: "warning",
            text: t("accounts.edit.coherence_warning"),
            detail,
            durationMs: 0,
          });
        }
      } else {
        pushToast({ kind: "success", text: t("accounts.edit.saved") });
      }
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("accounts.edit.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label>{t("accounts.edit.status")}</Label>
            <p className="text-xs text-fg-muted">
              {!pausable
                ? t("accounts.edit.status_locked_hint")
                : paused
                  ? t("accounts.status.paused")
                  : t(
                      `accounts.status.${
                        account.status === "warming_up" ? "warming_up" : "active"
                      }`,
                    )}
            </p>
          </div>
          <Switch
            checked={paused}
            disabled={!pausable}
            onCheckedChange={setPaused}
            aria-label={t("accounts.status.paused")}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("accounts.edit.role")}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AccountRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scraper">{t("accounts.role.scraper")}</SelectItem>
                <SelectItem value="inviter">{t("accounts.role.inviter")}</SelectItem>
                <SelectItem value="both">{t("accounts.role.both")}</SelectItem>
                {account.role === "demoted_scraper" && (
                  <SelectItem value="demoted_scraper" disabled>
                    {t("accounts.role.demoted_scraper")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {account.is_peer_flood_permanent && role !== "scraper" && role !== "demoted_scraper" && (
              <p className="text-xs text-warning">{t("accounts.edit.peer_flood_warning")}</p>
            )}
            {!account.warmup_completed && role === "scraper" && (
              <p className="text-xs text-fg-muted">{t("accounts.edit.role_after_warmup_hint")}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t("accounts.edit.proxy")}</Label>
            <Select value={proxyId} onValueChange={setProxyId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Shown only as the current state of an account that has no
                    proxy: an account can be moved to another proxy but never
                    detached from one — that would dial Telegram from our server. */}
                {!account.proxy_id && (
                  <SelectItem value="__none__" disabled>
                    {t("accounts.field.proxy_none")}
                  </SelectItem>
                )}
                {(proxies.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label} ({p.type}://{p.host}:{p.port})
                    {p.physical_type && p.physical_type !== "unknown"
                      ? ` · ${t(`proxies.physical_type.${p.physical_type}`)}`
                      : ""}
                    {p.is_alive === false ? ` · ${t("accounts.proxy_choice.dead")}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {account.recommended_proxy_type && (
              <p className="text-xs text-muted-foreground">
                {t("accounts.edit.recommended_proxy", {
                  type: t(`proxies.physical_type.${account.recommended_proxy_type}`),
                })}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t("accounts.edit.notes")}</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("accounts.edit.notes_placeholder")}
          />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-fg-muted">
            {dirty ? t("accounts.edit.dirty_hint") : t("accounts.edit.clean_hint")}
          </p>
          <Button size="sm" disabled={!dirty} loading={patch.isPending} onClick={save}>
            {t("accounts.edit.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleCard({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const maintenance = useMaintenanceStatus(account.id);
  const setMaintenance = useSetMaintenanceEnabled();
  const inbox = useInboxState(account.id);
  const setInbox = useSetInboxEnabled();

  const onMaintenance = (enabled: boolean) => {
    setMaintenance.mutate({ accountId: account.id, enabled });
  };
  const onInbox = (enabled: boolean) => {
    setInbox.mutate({ accountId: account.id, enabled });
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{t("accounts.maintenance.title")}</p>
            <p className="text-xs text-fg-muted">{t("accounts.maintenance.toggle_hint")}</p>
          </div>
          <Switch
            checked={maintenance.data?.enabled ?? false}
            onCheckedChange={onMaintenance}
            disabled={maintenance.isLoading || setMaintenance.isPending}
            aria-label={t("accounts.maintenance.title")}
          />
        </div>
        <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{t("accounts.inbox.title")}</p>
            <p className="text-xs text-fg-muted">{t("accounts.inbox.hint")}</p>
          </div>
          <Switch
            checked={inbox.data?.enabled ?? false}
            onCheckedChange={onInbox}
            disabled={inbox.isLoading || setInbox.isPending}
            aria-label={t("accounts.inbox.title")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function TgProfileCard({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const update = useUpdateTgProfile();
  const generate = useGenerateProfile();
  const [firstName, setFirstName] = useState(account.first_name ?? "");
  const [lastName, setLastName] = useState(account.last_name ?? "");
  const [username, setUsername] = useState(account.tg_username ?? "");
  // `about` is intentionally not seeded from the row (the API never returns the
  // current bio) — it starts empty and an empty value means "leave untouched".
  // This is the FIX for the bug where re-renders wiped it: we no longer re-init
  // first/last/username on every render, only when the underlying account
  // identity actually changes.
  const [about, setAbout] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(account.first_name ?? "");
    setLastName(account.last_name ?? "");
    setUsername(account.tg_username ?? "");
    setAbout("");
    setAvatar(null);
    setClearAvatar(false);
  }, [account.id, account.first_name, account.last_name, account.tg_username]);

  const firstChanged = firstName !== (account.first_name ?? "");
  const lastChanged = lastName !== (account.last_name ?? "");
  const usernameChanged = username !== (account.tg_username ?? "");
  const aboutChanged = about.length > 0;
  const dirty =
    firstChanged || lastChanged || usernameChanged || aboutChanged || avatar !== null || clearAvatar;

  const onGenerate = async () => {
    setError(null);
    try {
      const gp = await generate.mutateAsync({ lang: "ru" });
      setFirstName(gp.first_name);
      setLastName(gp.last_name);
      setAbout(gp.about);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onSave = async () => {
    setError(null);
    try {
      const res = await update.mutateAsync({
        id: account.id,
        first_name: firstChanged ? firstName : undefined,
        last_name: lastChanged ? lastName : undefined,
        username: usernameChanged ? username : undefined,
        about: aboutChanged ? about : undefined,
        avatar: avatar ?? undefined,
        clear_avatar: clearAvatar && !avatar ? true : undefined,
      });
      const parts: string[] = [];
      if (res.username_error) {
        parts.push(t("accounts.tg_profile.username_error", { reason: res.username_error }));
      }
      if (res.avatar_error) {
        parts.push(t("accounts.tg_profile.avatar_error", { reason: res.avatar_error }));
      }
      if (parts.length > 0) {
        pushToast({ kind: "warning", text: parts.join(" — ") });
      } else {
        pushToast({ kind: "success", text: t("accounts.tg_profile.saved") });
      }
      setAvatar(null);
      setClearAvatar(false);
      setAbout("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("accounts.tg_profile.title")}</CardTitle>
        <p className="text-sm text-fg-muted">{t("accounts.tg_profile.hint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`tgp-fn-${account.id}`}>{t("accounts.tg_profile.first_name")}</Label>
            <Input
              id={`tgp-fn-${account.id}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={64}
              placeholder={t("accounts.tg_profile.first_name_placeholder") ?? ""}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`tgp-ln-${account.id}`}>{t("accounts.tg_profile.last_name")}</Label>
            <Input
              id={`tgp-ln-${account.id}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={64}
              placeholder={t("accounts.tg_profile.last_name_placeholder") ?? ""}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`tgp-un-${account.id}`}>{t("accounts.tg_profile.username")}</Label>
            <Input
              id={`tgp-un-${account.id}`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={32}
              placeholder={t("accounts.tg_profile.username_placeholder") ?? ""}
            />
            <p className="text-xs text-fg-muted">{t("accounts.tg_profile.username_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`tgp-ab-${account.id}`}>{t("accounts.tg_profile.about")}</Label>
            <Input
              id={`tgp-ab-${account.id}`}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              maxLength={70}
              placeholder={t("accounts.tg_profile.about_placeholder") ?? ""}
            />
            <p className="text-xs text-fg-muted">{t("accounts.tg_profile.about_hint")}</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`tgp-av-${account.id}`}>{t("accounts.tg_profile.avatar")}</Label>
          <input
            id={`tgp-av-${account.id}`}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setAvatar(f);
              if (f) setClearAvatar(false);
            }}
            className="block w-full text-sm text-fg-secondary file:mr-3 file:rounded-md file:border-0 file:bg-bg-elevated file:px-3 file:py-1.5 file:text-sm file:text-fg-primary hover:file:bg-bg-elevated/70"
          />
          <label className="flex items-center gap-2 text-xs text-fg-muted">
            <input
              type="checkbox"
              checked={clearAvatar}
              onChange={(e) => {
                setClearAvatar(e.target.checked);
                if (e.target.checked) setAvatar(null);
              }}
            />
            {t("accounts.tg_profile.clear_avatar")}
          </label>
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            loading={generate.isPending}
            onClick={onGenerate}
          >
            {t("accounts.tg_profile.generate")}
          </Button>
          <Button size="sm" disabled={!dirty} loading={update.isPending} onClick={onSave}>
            {t("accounts.tg_profile.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
